import { http } from "./http";

// -------------------------
// Tipagens públicas
// -------------------------
export type CardholderIdentification = {
  type: "CPF" | "CNPJ" | string;
  number: string;
};

export type CardholderData = {
  name: string;
  identification?: CardholderIdentification;
};

export type CardDataForHash = {
  card_number: string;
  card_holder_name?: string;
  card_expiration_date: string; // MMYY
  card_cvv?: string;
};

export type BookingBillingAddress = {
  zipCode: string;
  street: string;
  number: string;
  complement?: string;
  neighborhood?: string;
  city: string;
  state: string;
  country?: string;
};

export type CardTokenResult = {
  type: "card_hash" | "card_token";
  value: string;
};

export type BookingCardPaymentPayload = {
  appointmentId?: string;
  establishmentId: string;
  transactionAmount: number;
  cardHash?: string;
  cardToken?: string;
  cardCvvStartsWithSix?: boolean;
  payerEmail: string;
  payerPhone?: string;
  payerCpf?: string;
  payerName?: string;
  description?: string;
  installments?: number;
  billingAddress?: BookingBillingAddress;
};

export type BookingPixPaymentPayload = {
  appointmentId?: string;
  establishmentId: string;
  transactionAmount: number;
  payerEmail: string;
  payerPhone?: string;
  payerCpf?: string;
  payerName?: string;
  description?: string;
  billingAddress?: BookingBillingAddress;
};

export type BookingCashPaymentPayload = {
  appointmentId: string;
  establishmentId: string;
  payerEmail?: string;
};

export type BookingPaymentResponse = {
  success?: boolean;
  providerPaymentId?: string;
  status?: string;
  message?: string;
  pix?: {
    qr_code?: string | null;
    qr_code_base64?: string | null;
    ticket_url?: string | null;
    expires_at?: string | null;
    id?: string | number | null;
    charge_id?: string | number | null;
  };
  [key: string]: any;
};

// -------------------------
// Config debug
// -------------------------
const NODE_ENV = process.env.NODE_ENV;
const DEBUG = NODE_ENV !== "production";

// -------------------------

// Pagar.me public (encryption) key
// -------------------------
export function getPagarmeEncryptionKey(
  publicKeyOverride?: string | null
): string | null {
  const override = String(publicKeyOverride || "").trim();
  if (override) return override;

  const isProd = NODE_ENV === "production";
  const key = isProd
    ? process.env.NEXT_PUBLIC_PAGARME_ENCRYPTION_KEY ||
      process.env.NEXT_PUBLIC_PAGARME_ENCRYPTION_KEY_PROD
    : process.env.NEXT_PUBLIC_PAGARME_ENCRYPTION_KEY_TEST ||
      process.env.NEXT_PUBLIC_PAGARME_ENCRYPTION_KEY;
  return key || null;
}

let _pagarmeReady = false;

const DEFAULT_PAGARME_SDK_URLS = [
  "https://assets.pagar.me/pagarme-js/4.14.0/pagarme.min.js",
  "https://assets.pagar.me/pagarme-js/4.13.0/pagarme.min.js",
];

function getPagarmeSdkUrls() {
  const custom = process.env.NEXT_PUBLIC_PAGARME_JS_URL;
  if (custom) {
    return [custom, ...DEFAULT_PAGARME_SDK_URLS.filter((u) => u !== custom)];
  }
  return DEFAULT_PAGARME_SDK_URLS;
}

function getPagarmeGlobal(): any {
  if (typeof window === "undefined") return null;
  return (window as any).pagarme || (window as any).Pagarme || (window as any).PagarMe;
}

async function ensureScriptLoaded(src: string): Promise<void> {
  const existing = document.querySelector(`script[src="${src}"]`);
  if (existing) return;

  await new Promise<void>((resolve, reject) => {
    const s = document.createElement("script");
    s.src = src;
    s.async = true;
    s.crossOrigin = "anonymous";
    s.onload = () => resolve();
    s.onerror = () =>
      reject(
        new Error("Não foi possível carregar o ambiente de pagamento. Tente novamente.")
      );
    document.head.appendChild(s);
  });
}

export async function preloadPagarmeSdk(timeout = 15000): Promise<void> {
  if (typeof window === "undefined") return;
  if (_pagarmeReady && getPagarmeGlobal()) return;

  const urls = getPagarmeSdkUrls();

  for (const src of urls) {
    try {
      await ensureScriptLoaded(src);
    } catch (err) {
      if (DEBUG) console.warn("Falha ao carregar pagarme-js", src, err);
      continue;
    }

    const start = Date.now();
    while (!getPagarmeGlobal()) {
      if (Date.now() - start > timeout) break;
      await new Promise((r) => setTimeout(r, 80));
    }

    if (getPagarmeGlobal()) {
      _pagarmeReady = true;
      return;
    }
  }

  throw new Error("Não foi possível carregar o ambiente de pagamento. Tente novamente.");
}

function parseExpiration(card: CardDataForHash) {
  const exp = String(card.card_expiration_date || "").replace(/\D/g, "");
  if (exp.length < 4) {
    throw new Error("Data de expiração inválida.");
  }
  const expMonth = exp.slice(0, 2);
  const expYear = exp.slice(-2);
  return {
    exp_month: Number(expMonth),
    exp_year: Number(expYear) + 2000,
  };
}

function maskCardNumber(number: string): string {
  const digits = String(number || "").replace(/\D/g, "");
  if (!digits) return "";
  if (digits.length <= 4) return "*".repeat(digits.length);
  return "*".repeat(Math.max(digits.length - 4, 0)) + digits.slice(-4);
}

function collectProviderErrorMessages(value: unknown): string[] {
  if (typeof value === "string") {
    const normalized = value.trim();
    return normalized ? [normalized] : [];
  }
  if (Array.isArray(value)) {
    return value.flatMap((item) => collectProviderErrorMessages(item));
  }
  if (value && typeof value === "object") {
    return Object.entries(value as Record<string, unknown>).flatMap(([key, nested]) => {
      const nestedMessages = collectProviderErrorMessages(nested);
      if (nestedMessages.length === 0) return [];
      return nestedMessages.map((message) => key + ": " + message);
    });
  }
  return [];
}

function normalizeTokenizationErrorMessage(rawMessage: unknown, data: any): string {
  const fallback = String(rawMessage || "").trim();
  const providerMessage = String(data?.message || data?.error || "").trim();
  const details = collectProviderErrorMessages(data?.errors || data?.details || null);

  if (details.length > 0) {
    return "Dados do cartão inválidos. " + details.join(" | ");
  }

  if (/the request is invalid/i.test(providerMessage || fallback)) {
    return "Dados do cartão inválidos. Verifique número, validade e CVV.";
  }

  return (
    providerMessage ||
    fallback ||
    "Não foi possível validar o cartão. Revise os dados e tente novamente."
  );
}

async function createCardTokenViaApi(
  card: CardDataForHash,
  publicKey: string
): Promise<string> {
  const { exp_month, exp_year } = parseExpiration(card);
  const cardNumberDigits = String(card.card_number || "").replace(/\D/g, "");
  const cardCvvDigits = String(card.card_cvv || "").replace(/\D/g, "");
  const holderName = String(card.card_holder_name || "").trim();

  if (cardNumberDigits.length < 12 || cardNumberDigits.length > 19) {
    throw new Error("Número do cartão inválido.");
  }
  if (holderName.length < 2) {
    throw new Error("Nome do titular inválido.");
  }
  if (cardCvvDigits.length < 3 || cardCvvDigits.length > 4) {
    throw new Error("CVV inválido.");
  }
  if (
    !Number.isFinite(exp_month) ||
    exp_month < 1 ||
    exp_month > 12 ||
    !Number.isFinite(exp_year) ||
    exp_year < 2000
  ) {
    throw new Error("Data de expiração inválida.");
  }

  const now = new Date();
  const nowMonth = now.getMonth() + 1;
  const nowYear = now.getFullYear();
  if (exp_year < nowYear || (exp_year === nowYear && exp_month < nowMonth)) {
    throw new Error("Cartão vencido.");
  }

  const payload = {
    type: "card",
    card: {
      number: cardNumberDigits,
      holder_name: holderName,
      exp_month,
      exp_year,
      cvv: cardCvvDigits,
    },
  };

  if (DEBUG) {
    console.log("[PAGARME][TOKEN] criando token de cartão", {
      number: maskCardNumber(cardNumberDigits),
      exp_month,
      exp_year,
      hasCvv: Boolean(cardCvvDigits),
      holderNameLength: holderName.length,
    });
  }

  const url = `https://api.pagar.me/core/v5/tokens?appId=${encodeURIComponent(
    publicKey
  )}`;
  const resp = await fetch(url, {
    method: "POST",
    headers: {
      "content-type": "application/json",
      accept: "application/json",
    },
    body: JSON.stringify(payload),
  });

  let data: any = null;
  try {
    data = await resp.json();
  } catch {
    data = null;
  }

  if (!resp.ok) {
    const msg = normalizeTokenizationErrorMessage(
      data?.message ||
        data?.error ||
        "Não foi possível validar o cartão. Revise os dados e tente novamente.",
      data
    );
    if (DEBUG) {
      console.warn("[PAGARME][TOKEN] falha ao tokenizar cartão", {
        status: resp.status,
        response: data || null,
      });
    }
    throw new Error(msg);
  }

  const token = data?.id || data?.token || null;
  if (!token) {
    throw new Error(
      "Não foi possível validar o cartão. Revise os dados e tente novamente."
    );
  }
  return token;
}

export async function createCardToken(
  card: CardDataForHash,
  publicKeyOverride?: string | null
): Promise<string> {
  const key = getPagarmeEncryptionKey(publicKeyOverride);
  if (!key) throw new Error("Chave pública (encryption_key) da Stone não configurada");
  return createCardTokenViaApi(card, key);
}

export async function createCardHash(
  card: CardDataForHash,
  publicKeyOverride?: string | null
): Promise<CardTokenResult> {
  const key = getPagarmeEncryptionKey(publicKeyOverride);
  if (!key) throw new Error("Chave pública (encryption_key) da Stone não configurada");

  const token = await createCardTokenViaApi(card, key);
  return { type: "card_token", value: token };
}

// -------------------------
// Helpers para backend Pagar.me
// -------------------------
export function payWithCard(payload: BookingCardPaymentPayload) {
  return http<BookingPaymentResponse>("/payments/card", {
    method: "POST",
    body: payload,
  });
}

export function payWithPix(payload: BookingPixPaymentPayload) {
  return http<BookingPaymentResponse>("/payments/pix", { method: "POST", body: payload });
}

export function confirmCashPayment(payload: BookingCashPaymentPayload) {
  return http<{ ok: boolean; status?: string }>("/payments/cash", {
    method: "POST",
    body: payload,
  });
}

export function confirmPixPayment(payload: {
  establishmentId: string;
  appointmentId: string;
  providerPaymentId: string;
}) {
  return http<any>("/payments/confirm-pix", { method: "POST", body: payload });
}

export function getOnlinePaymentsStatus(establishmentId: string) {
  return http(`/payments/status/${establishmentId}`, { method: "GET" });
}

export type PriceQuoteParams = {
  establishmentId: string;
  paymentType: "credit" | "debit" | "pix" | "cash" | "boleto";
  serviceId?: string;
  serviceIds?: string[];
  creditPayoutTerm?: string;
  amount?: number;
  amountCents?: number;
  /** Calculates the configured fee schedule without enabling online checkout. */
  previewFees?: boolean;
};

export type PriceQuoteResponse = {
  serviceAmount: number;
  finalAmount: number;
  feeAmount: number;
  passCardFeesToClient: "SIM" | "NÃO";
  gatewayFeePercentage?: number;
  systemFeePercentage?: number;
  totalFeePercentage?: number;
  /** Em retenção, o preço não cobre comissão + taxas fixas: não é cobrável. */
  belowMinimum?: boolean;
  card?: {
    finalAmount?: number;
    feeAmount?: number;
    totalFeePercentage?: number;
    gatewayFeePercentage?: number;
    cardFinalAmount?: number;
    cardFeeAmount?: number;
    cardFeePercentage?: number;
    cardTotalFeePercentage?: number;
    mdrFeeValue?: number;
    anticipationFeeValue?: number;
    gatewayFeeValue?: number;
    antifraudFeeValue?: number;
    systemFeeValue?: number;
    netAmount?: number;
    remainderCents?: number;
    installments?: number;
  };
  pix?: {
    pixFinalAmount?: number;
    pixFeeAmount?: number;
    pixFeePercentage?: number;
    pixTotalFeePercentage?: number;
    pixMdrFeeValue?: number;
    pixSystemFeeValue?: number;
    pixGatewayFee?: number;
    pixSystemFee?: number;
    pixNetAmount?: number;
    pixRemainderCents?: number;
    subtotal?: number;
    total?: number;
  };
  boleto?: {
    finalAmount?: number;
    feeAmount?: number;
    totalFeePercentage?: number;
    boletoFeeAmount?: number;
    boletoRefundFeeAmount?: number;
    systemFeeAmount?: number;
    netAmount?: number;
  };
};

const PRICE_QUOTE_CACHE_TTL_MS = 15_000;
const priceQuoteCache = new Map<
  string,
  { expiresAt: number; promise: Promise<PriceQuoteResponse> }
>();

function normalizePriceQuoteParams(params: PriceQuoteParams): PriceQuoteParams {
  return {
    ...params,
    serviceIds: Array.isArray(params.serviceIds)
      ? [...params.serviceIds].map(String).sort()
      : params.serviceIds,
  };
}

function buildPriceQuoteCacheKey(params: PriceQuoteParams) {
  return JSON.stringify(normalizePriceQuoteParams(params));
}

export function getPriceQuote(params: PriceQuoteParams) {
  const cacheKey = buildPriceQuoteCacheKey(params);
  const now = Date.now();
  const cached = priceQuoteCache.get(cacheKey);

  if (cached && cached.expiresAt > now) {
    return cached.promise;
  }

  const promise = http<PriceQuoteResponse>("/payments/price-quote", {
    method: "POST",
    body: normalizePriceQuoteParams(params),
  });

  priceQuoteCache.set(cacheKey, {
    expiresAt: now + PRICE_QUOTE_CACHE_TTL_MS,
    promise,
  });

  promise.catch(() => {
    if (priceQuoteCache.get(cacheKey)?.promise === promise) {
      priceQuoteCache.delete(cacheKey);
    }
  });

  return promise;
}

export function __clearPriceQuoteCacheForTests() {
  priceQuoteCache.clear();
}
